// frontend/src/modules/reports/registry.ts
//
// Phase 4 (Reports Frontend). Shared registry-entry shape for both report
// families (workforce/registry.ts, financial/registry.ts). ReportsPage
// renders each tab purely from its registry — this is the file-ownership
// seam from the plan: T2/T3 append ONLY to workforce/registry.ts, T4 append
// ONLY to financial/registry.ts, so parallel tasks never touch the same
// file.
//
// Visibility rule per report (fail closed to hidden): the tenant must have
// EVERY module in `modules` enabled AND the user must hold AT LEAST ONE key
// in `permissions` — EXCEPT the two compound reports (project-profitability,
// pnl-lite) which need ALL of their listed permission keys (see
// `requiresAllPermissions` below). `reports.view` is checked once, at the
// ReportsPage level, not per-entry — every report implicitly requires it.

import type { ComponentType } from 'react';

export interface ReportRegistryEntry {
  /** Matches the backend report's URL segment (e.g. 'hours-summary'). */
  key: string;
  title: string;
  description: string;
  /** Underlying-data permission key(s) gating this report (mirrors the
   * backend's requireFamily(...) stack, minus reports.view). */
  permissions: string[];
  /** Module key(s) that must be enabled for this report to be visible. */
  modules: string[];
  /** True for the two compound reports that need ALL permission keys
   * (project-profitability: projects.reports + invoices.view; pnl-lite:
   * invoices.view + expenses.read + payroll.view). Defaults to "any one". */
  requiresAllPermissions?: boolean;
  component: ComponentType;
}

/** Visible iff every module is enabled AND the permission rule is satisfied. */
export function isReportVisible(
  entry: ReportRegistryEntry, modules: Set<string>, perms: Set<string>,
): boolean {
  const modulesOk = entry.modules.every((m) => modules.has(m));
  if (!modulesOk) return false;
  if (perms.has('*')) return true;
  return entry.requiresAllPermissions
    ? entry.permissions.every((p) => perms.has(p))
    : entry.permissions.some((p) => perms.has(p));
}
